import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiDownload, FiCopy, FiRefreshCw, FiSettings, FiSave, FiShare2 } = FiIcons;

const ToolInterface = ({ 
  tool, 
  children, 
  onGenerate, 
  isGenerating, 
  result, 
  onCopy, 
  onDownload, 
  onSave 
}) => {
  const [showSettings, setShowSettings] = useState(false);

  return (
    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl border border-gray-700/50 backdrop-blur-sm overflow-hidden">
      {/* Tool Header */}
      <div className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 p-6 border-b border-gray-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              tool?.isPremium 
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500' 
                : 'bg-gradient-to-r from-green-500 to-emerald-500'
            }`}>
              <SafeIcon icon={tool?.icon} className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-orbitron font-bold text-white">{tool?.name}</h2>
              <p className="text-gray-300 font-poppins text-sm">{tool?.description}</p>
            </div>
          </div>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
          >
            <SafeIcon icon={FiSettings} className="text-gray-300" />
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-gray-800/30 p-4 border-b border-gray-700/50"
        >
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-poppins text-gray-300 mb-2">Tone</label>
              <select className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2 text-white font-poppins text-sm">
                <option>Professional</option>
                <option>Casual</option>
                <option>Friendly</option>
                <option>Authoritative</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-poppins text-gray-300 mb-2">Length</label>
              <select className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2 text-white font-poppins text-sm">
                <option>Short</option>
                <option>Medium</option>
                <option>Long</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-poppins text-gray-300 mb-2">Style</label>
              <select className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2 text-white font-poppins text-sm">
                <option>Creative</option>
                <option>Direct</option>
                <option>Storytelling</option>
                <option>Technical</option>
              </select>
            </div>
          </div>
        </motion.div>
      )}

      {/* Tool Content */}
      <div className="p-6">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div>
            <h3 className="text-lg font-orbitron font-semibold text-white mb-4">Input</h3>
            {children}
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onGenerate}
              disabled={isGenerating}
              className={`w-full mt-4 py-3 rounded-lg font-poppins font-semibold transition-all duration-300 flex items-center justify-center space-x-2 ${
                tool?.isPremium
                  ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white glow-button'
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white glow-button'
              } ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <SafeIcon icon={isGenerating ? FiRefreshCw : FiPlay} className={isGenerating ? 'animate-spin' : ''} />
              <span>{isGenerating ? 'Generating...' : 'Generate'}</span>
            </motion.button>
          </div>

          {/* Output Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-orbitron font-semibold text-white">Output</h3>
              {result && (
                <div className="flex items-center space-x-2">
                  {onCopy && (
                    <button
                      onClick={onCopy}
                      className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
                      title="Copy to clipboard"
                    >
                      <SafeIcon icon={FiCopy} className="text-gray-300" />
                    </button>
                  )}
                  {onDownload && (
                    <button
                      onClick={onDownload}
                      className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
                      title="Download"
                    >
                      <SafeIcon icon={FiDownload} className="text-gray-300" />
                    </button>
                  )}
                  {onSave && (
                    <button
                      onClick={onSave}
                      className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
                      title="Save"
                    >
                      <SafeIcon icon={FiSave} className="text-gray-300" />
                    </button>
                  )}
                  <button
                    className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors"
                    title="Share"
                  >
                    <SafeIcon icon={FiShare2} className="text-gray-300" />
                  </button>
                </div>
              )}
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-4 min-h-[200px] border border-gray-700/50">
              {result ? (
                <div className="text-gray-200 font-poppins whitespace-pre-wrap">
                  {result}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 font-poppins">
                  Your generated content will appear here
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolInterface;